import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConditionalFilterPlugin,
  FileData,
  FileSelection,
  GoogleFontIcon,
  InlineAlert,
  isFileSelection,
  jsonEditorWidgets,
  Modal,
  SVGIcon,
  Upload,
} from '@asset-metrix/portal-ui';
import {
  Editor,
  isJsonError,
  JsonError,
  JsonSchema,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import {
  METADATA_EXTRACTION_FROM_AI_VALUE,
  METADATA_EXTRACTION_FROM_FILE_NAME_VALUE,
  METADATA_EXTRACTION_FROM_USER_VALUE,
  METADATA_EXTRACTION_TYPE,
} from './constants';
import {
  useFetchAIMetadataForAllFiles,
  useGenerateSchemaDisablingMetaDataExtractionKeys,
  useGenerateSchemaForMetaDataExtraction,
  useGetUserFilteredData,
  useMetadataInputsDisabled,
  usePreValidateFiles,
} from './hooks';
import { MetadataExtractionConfiguration } from './MetadataExtractionConfiguration';
import { RenderMetaDataExtractedView } from './RenderMetaDataExtractedView';
import { collectStep2PropertyKeys } from './step2SchemaProperties';
import { useDocUploadStore } from './store';

import styles from './doc-upload-modal.module.scss';

const getSelectionsForConditionalFilters = ({
  editorJsonSchema,
  editor,
  excludeKeys,
}: {
  editorJsonSchema: JsonSchema | undefined;
  editor: Editor | null;
  excludeKeys: Set<string>;
}) => {
  const filter: Record<string, string | string[] | null> = {};
  const editorData = editor?.getData();
  if (
    editorJsonSchema?.properties &&
    typeof editorData === 'object' &&
    Object.keys(editorData).length > 0
  ) {
    for (const [key, schemaProperty] of Object.entries(
      editorJsonSchema?.properties
    )) {
      const data = editorData[key];
      if (
        typeof schemaProperty === 'object' &&
        // dataGroup indicates that the field is conditionally filtered (dataProperty also exists on non-conditional filtered fields)
        schemaProperty.conditionalFilter?.dataGroup &&
        !excludeKeys.has(key) &&
        ((typeof data === 'string' && data.length > 0) ||
          (Array.isArray(data) &&
            data.length > 0 &&
            data.every((d) => typeof d === 'string')))
      ) {
        filter[schemaProperty.conditionalFilter.dataProperty] = data;
      }
    }
  }
  return filter;
};

type ViewTypes = 'upload' | 'validate';

export function MultiStepUpload() {
  const { t } = useTranslation();

  const {
    aiExtractionEnabled,
    fileNameMetadataExtractionEnabled,
    acceptedMimeTypes,
    enablePatternBasedRenaming: configEnablePatternBasedRenaming,
    tableModalValues,
    cleanupAndClose,
    contentId,
    tableFilterInstanceId,
    dispatchFileStateAction,
    editorErrors,
    fileNameMetadataExtractionConfig,
    fetchOptions,
    fileState,
    initializeRenameFileWithPattern,
    isTableModalValuesLoading,
    jsonSchema,
    modalError,
    onUploadSuccess,
    perFileMetaData,
    setAiExtractionExplanations,
    setEditorErrors,
    setPatternBasedFilename,
    setperFileMetaData,
    uploadAllFiles,
    uploadSingleFile,
    withinPortal,
  } = useDocUploadStore(
    useShallow((state) => ({
      aiExtractionEnabled: state.aiExtractionEnabled,
      fileNameMetadataExtractionEnabled:
        state.fileNameMetadataExtractionEnabled,
      acceptedMimeTypes: state.acceptedMimeTypes,
      enablePatternBasedRenaming: state.enablePatternBasedRenaming,
      tableModalValues: state.tableModalValues,
      cleanupAndClose: state.cleanupAndClose,
      contentId: state.contentId,
      tableFilterInstanceId: state.tableFilterInstanceId,
      dispatchFileStateAction: state.dispatchFileStateAction,
      editorErrors: state.editorErrors,
      fileNameMetadataExtractionConfig: state.fileNameMetadataExtractionConfig,
      fetchOptions: state.fetchOptions,
      fileState: state.fileState,
      initializeRenameFileWithPattern: state.initializeRenameFileWithPattern,
      isTableModalValuesLoading: state.isTableModalValuesLoading,
      jsonSchema: state.jsonSchema,
      modalError: state.modalError,
      onUploadSuccess: state.onUploadSuccess,
      perFileMetaData: state.perFileMetaData,
      setAiExtractionExplanations: state.setAiExtractionExplanations,
      setEditorErrors: state.setEditorErrors,
      setPatternBasedFilename: state.setPatternBasedFilename,
      setperFileMetaData: state.setperFileMetaData,
      uploadAllFiles: state.uploadAllFiles,
      uploadSingleFile: state.uploadSingleFile,
      withinPortal: state.withinPortal,
    }))
  );

  // ai-metadata-extraction component specific
  const [userEnabledPatternBasedRenaming, setUserEnabledPatternBasedRenaming] =
    useState(configEnablePatternBasedRenaming);

  // Stable editor data captured once when entering the validate step (see validateViewPreFilter below).
  const [validateViewEditorData, setValidateViewEditorData] = useState<
    Record<string, unknown>
  >({});
  const [validateViewPreFilter, setValidateViewPreFilter] = useState<
    Record<string, string | string[] | null>
  >({});

  const [extractionMethod, setExtractionMethodInternal] =
    useState<METADATA_EXTRACTION_TYPE>(() => {
      if (aiExtractionEnabled) {
        return METADATA_EXTRACTION_FROM_AI_VALUE;
      }
      if (fileNameMetadataExtractionEnabled) {
        return METADATA_EXTRACTION_FROM_FILE_NAME_VALUE;
      }
      return METADATA_EXTRACTION_FROM_USER_VALUE;
    });

  // Track previous extraction method to detect changes
  const prevExtractionMethodRef = useRef(extractionMethod);

  // Clear validation errors, metadata extraction errors, and editor data
  // when extraction method changes, so files get re-validated with the new schema
  useEffect(() => {
    if (prevExtractionMethodRef.current !== extractionMethod) {
      dispatchFileStateAction({ type: 'clearAllValidationErrors' });
      setperFileMetaData({});
      prevExtractionMethodRef.current = extractionMethod;
    }
  }, [extractionMethod, dispatchFileStateAction, setperFileMetaData]);

  // we keep a copy of jsonSchema from jsonEditor as it will have extra properties from the plugins.
  // eg Conditional filter plugin will add enum from the api data,
  // and we need to retain this information when the extractionMethod changes to show dropDown options
  const [editorJsonSchema, setEditorJsonSchema] = useState(jsonSchema);

  const [currentView, setCurrentViewInternal] = useState<ViewTypes>('upload');
  const isCurrentView = useCallback(
    (viewType: ViewTypes) => viewType === currentView,
    [currentView]
  );

  const setErrors = useCallback(
    (fieldId: string, error: JsonError[]) => {
      dispatchFileStateAction({
        type: 'replaceFileValidationErrors',
        payload: { id: fieldId, validationErrors: error },
      });
    },
    [dispatchFileStateAction]
  );

  // if metadata extraction is enabled this hook returns a new jsonSchema with metadata key disabled.
  // else it will return orignal jsonSchema
  const jsonSchemaWithoutMetaDataExtractionKey =
    useGenerateSchemaDisablingMetaDataExtractionKeys({
      fileNameMetadataExtractionConfig,
      editorJsonSchema,
      extractionMethod,
      jsonSchema,
    });
  // this hook retuns 2 jsonSchema useFull for next step (check file specific metadata)
  const { jsonSchemaWithFileNameExtraction, jsonSchemaWithAIExtraction } =
    useGenerateSchemaForMetaDataExtraction({
      fileNameMetadataExtractionConfig,
      jsonSchema,
    });

  // Determine which schema to use for validation based on extraction method
  const validationJsonSchema =
    extractionMethod === METADATA_EXTRACTION_FROM_FILE_NAME_VALUE
      ? jsonSchemaWithFileNameExtraction
      : jsonSchemaWithAIExtraction;

  const initialData = useGetUserFilteredData(
    jsonSchemaWithoutMetaDataExtractionKey,
    tableFilterInstanceId
  );

  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: jsonSchemaWithoutMetaDataExtractionKey,
    onChange: (_data, _root, editor) => {
      // Defer state updates to avoid setState-during-render warning.
      // useEditor fires onChange synchronously during render when the schema
      // prop changes (e.g. on extraction-method switch); queueMicrotask pushes
      // the updates past the current render cycle while still batching them.
      queueMicrotask(() => {
        setEditorErrors(editor.getErrors());
        const schema = editor.getSchema();
        schema && !isJsonError(schema) && setEditorJsonSchema(schema);
      });
    },
    validate: true,
    data: initialData,
  });
  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions,
  });

  // Pre-validation hook for validating all files when entering validate step
  const { preValidateAllFiles } = usePreValidateFiles({
    tableModalValues,
    fileNameMetadataExtractionConfig,
    editorInitialData: editor?.getData() ?? {},
    extractionMethod,
    fileState,
    jsonSchema: validationJsonSchema,
    setErrors,
    perFileMetaData,
  });
  // Handler for metadata editor changes in validate view
  const onMetaDataEditorChange = useCallback(
    (fileId: string, data: Record<string, unknown>) => {
      setperFileMetaData((prev) => ({ ...prev, [fileId]: data }));
      if (userEnabledPatternBasedRenaming) {
        setPatternBasedFilename(fileId, data);
      }
    },
    [
      userEnabledPatternBasedRenaming,
      setPatternBasedFilename,
      setperFileMetaData,
    ]
  );

  // Hook to fetch AI metadata for all PDF files (called when entering validate view with AI extraction)
  const { fetchAIMetadataForAllFiles } = useFetchAIMetadataForAllFiles({
    contentId,
    fileStateDispatch: dispatchFileStateAction,
    fileState,
  });

  // Wrapper for setCurrentView that triggers pre-validation when entering validate step
  const setCurrentView = useCallback(
    (view: ViewTypes) => {
      if (view === 'validate') {
        // Pre-validate files and compute metadata extraction errors for from_file and from_user
        preValidateAllFiles();

        // Capture conditional selections once when entering validate view so the per-file editors
        // keep a stable preFilter across subsequent re-renders. Exclude step-2 fields so clearing
        // them in the per-file editor can fully unfilter options.
        const nextValidateViewPreFilter = getSelectionsForConditionalFilters({
          editorJsonSchema,
          editor,
          excludeKeys: collectStep2PropertyKeys(jsonSchema, {
            extractionMode:
              extractionMethod === METADATA_EXTRACTION_FROM_FILE_NAME_VALUE
                ? METADATA_EXTRACTION_FROM_FILE_NAME_VALUE
                : METADATA_EXTRACTION_FROM_AI_VALUE,
            fileNameMetadataExtractionKey:
              fileNameMetadataExtractionConfig?.key,
          }),
        });
        setValidateViewPreFilter(nextValidateViewPreFilter);

        // Capture editor data once so editorInitialData passed to RenderMetaDataExtractedView
        // stays stable across subsequent re-renders (avoids regenerating generateMetaDataExtractionInput
        // and retriggering the JSON editor's onChange on every fileState update).
        setValidateViewEditorData(editor?.getData() ?? {});

        // Set view first (show validate view immediately)
        setCurrentViewInternal(view);

        // Initialize renameFileWithPattern.
        initializeRenameFileWithPattern(userEnabledPatternBasedRenaming);

        // For AI extraction, trigger async fetch after view is set (maintains current UX with per-file loading)
        if (extractionMethod === METADATA_EXTRACTION_FROM_AI_VALUE) {
          fetchAIMetadataForAllFiles(
            nextValidateViewPreFilter,
            jsonSchemaWithAIExtraction,
            onMetaDataEditorChange,
            setAiExtractionExplanations
          );
        }
        return;
      }
      setCurrentViewInternal(view);
    },
    [
      editor,
      editorJsonSchema,
      extractionMethod,
      fetchAIMetadataForAllFiles,
      fileNameMetadataExtractionConfig?.key,
      initializeRenameFileWithPattern,
      jsonSchema,
      jsonSchemaWithAIExtraction,
      onMetaDataEditorChange,
      preValidateAllFiles,
      userEnabledPatternBasedRenaming,
      setAiExtractionExplanations,
      setValidateViewEditorData,
    ]
  );

  const onFileRemove = useCallback(
    (fileData: FileData) => {
      if (isFileSelection(fileData)) {
        dispatchFileStateAction({
          type: 'removeFileSelections',
          payload: [fileData.id],
        });
      }
    },
    [dispatchFileStateAction]
  );

  const uploadFile = useCallback(
    async (fileSelection: FileSelection) => {
      const uploaded = await uploadSingleFile(fileSelection, editor?.getData());
      // only trigger onUploadSuccess if file is uploaded successfully.
      if (uploaded) {
        await onUploadSuccess?.();
      }
    },
    [editor, uploadSingleFile, onUploadSuccess]
  );

  const metadataInputsDisabled = useMetadataInputsDisabled();

  const isFileStateInitial = useMemo(
    () => fileState.every((state) => state.uploadState === 'initial'),
    [fileState]
  );

  const isFileStatePending = useMemo(
    () => fileState.some((state) => state.uploadState === 'isPending'),
    [fileState]
  );

  const isFileStateFetchingMetadata = useMemo(
    () => fileState.some((state) => state.uploadState === 'isFetchingMetadata'),
    [fileState]
  );

  const isUploadDisabled = useMemo(
    () =>
      editorErrors.length > 0 ||
      fileState.length === 0 ||
      Boolean(isTableModalValuesLoading) ||
      !!modalError,
    [
      editorErrors.length,
      fileState.length,
      isTableModalValuesLoading,
      modalError,
    ]
  );

  const renderPrimaryButton = () => {
    // files have uploaded or loading
    if (!isFileStateInitial) {
      return (
        <Button
          icon={
            isFileStatePending || isFileStateFetchingMetadata ? (
              <SVGIcon name="upload" />
            ) : (
              <GoogleFontIcon name="check" />
            )
          }
          onClick={cleanupAndClose}
          disabled={isFileStatePending || isFileStateFetchingMetadata}
        >
          {t(
            isFileStatePending
              ? 'app.upload.uploading'
              : isFileStateFetchingMetadata
                ? 'app.upload.fetchingMetadata'
                : 'app.upload.okay'
          )}
        </Button>
      );
    }

    if (isCurrentView('upload')) {
      return (
        <Button
          icon={<GoogleFontIcon name="receipt_long" />}
          onClick={() => setCurrentView('validate')}
          disabled={isUploadDisabled}
        >
          {t('app.upload.extractMetaData.checkBtnLabel')}
        </Button>
      );
    }

    const areMetaDataFieldsValid =
      fileState.length > 0 &&
      fileState.every(
        (file) =>
          file.validationErrors !== undefined &&
          file.validationErrors.length === 0
      );

    return (
      <Button
        data-testid="doc-upload-validate-upload"
        icon={<GoogleFontIcon name="receipt_long" />}
        onClick={() => uploadAllFiles(editor?.getData())}
        disabled={isUploadDisabled || !areMetaDataFieldsValid}
      >
        {t('app.upload.upload')}
      </Button>
    );
  };

  const renderCurrentStep = () => {
    if (isCurrentView('upload')) {
      return (
        <div className={styles['content']}>
          <Upload
            accept={acceptedMimeTypes}
            fileState={fileState}
            dispatch={dispatchFileStateAction}
            uploadFile={uploadFile}
            fileNameMetadataExtractionConfig={fileNameMetadataExtractionConfig}
          />
          <div
            className={clsx(
              'portal-form rje-form rje-theme rje-theme--light',
              styles['editor']
            )}
          >
            <MetadataExtractionConfiguration
              extractionMethod={extractionMethod}
              inputsDisabled={metadataInputsDisabled}
              userEnabledPatternBasedRenaming={userEnabledPatternBasedRenaming}
              setExtractionMethod={setExtractionMethodInternal}
              setUserEnabledPatternBasedRenaming={
                setUserEnabledPatternBasedRenaming
              }
            />
            <Widget
              editor={editor}
              options={{
                disabled: metadataInputsDisabled,
                loading: Boolean(isTableModalValuesLoading),
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <RenderMetaDataExtractedView
        editorInitialData={validateViewEditorData}
        extractionMethod={extractionMethod}
        jsonSchema={
          extractionMethod === METADATA_EXTRACTION_FROM_FILE_NAME_VALUE
            ? jsonSchemaWithFileNameExtraction
            : jsonSchemaWithAIExtraction
        }
        onEditorDataChange={onMetaDataEditorChange}
        onFileRemove={onFileRemove}
        userEnabledPatternBasedRenaming={userEnabledPatternBasedRenaming}
        preFilter={validateViewPreFilter}
        setErrors={setErrors}
        upload={uploadFile}
      />
    );
  };

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={cleanupAndClose}
            disabled={!isFileStateInitial && !isFileStateFetchingMetadata}
          >
            {t('app.upload.cancel')}
          </Button>
          {renderPrimaryButton()}
        </>
      }
      contentClassName={styles['main-content']}
      contentHeight="95vh"
      modalType="action"
      opened={true}
      onClose={cleanupAndClose}
      size="95vw"
      title={t('app.upload.zipUploadModal.headline')}
      withinPortal={withinPortal}
    >
      {modalError && (
        <InlineAlert className={styles['alert']}>{modalError}</InlineAlert>
      )}
      {renderCurrentStep()}
    </Modal>
  );
}
